import {
  createZemChargeFlowId,
  isZemChargeFlowId,
  ZEM_CHARGE_BALANCE_ZEM_PARAM,
  ZEM_CHARGE_BALANCE_ZEM_STORAGE_KEY,
  ZEM_CHARGE_COMMERCE_FLOW_ID_PARAM,
  ZEM_CHARGE_COMMERCE_FLOW_ID_STORAGE_KEY,
  ZEM_CHARGE_FLOW_ID_PARAM,
  ZEM_CHARGE_FLOW_ID_STORAGE_KEY,
  ZEM_CHARGE_POPUP_MODE,
  ZEM_CHARGE_POPUP_OPENER_ORIGIN_PARAM,
  ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY,
  ZEM_CHARGE_POPUP_PLACE_PARAM,
  ZEM_CHARGE_POPUP_PLACE_STORAGE_KEY,
  ZEM_CHARGE_POPUP_PLACES,
  ZEM_CHARGE_REASON_PARAM,
  ZEM_CHARGE_REASON_STORAGE_KEY,
  ZEM_CHARGE_REASONS,
  ZEM_CHARGE_REQUIRED_ZEM_PARAM,
  ZEM_CHARGE_REQUIRED_ZEM_STORAGE_KEY,
  ZEM_CHARGE_SHORTFALL_ZEM_PARAM,
  ZEM_CHARGE_SHORTFALL_ZEM_STORAGE_KEY,
  type ZemChargePopupPlace,
  type ZemChargeReason,
} from '@zep/common/routes';

export type ZemChargePopupContext = {
  query: Record<string, string>;
  chargeFlowId: string;
  commerceFlowId?: string;
  place?: ZemChargePopupPlace;
  chargeReason?: ZemChargeReason;
  requiredZem?: number;
  balanceZem?: number;
  shortfallZem?: number;
};

const COMPLETE_TRACKED_STORAGE_PREFIX = 'zep-zem-charge-complete-tracked:';
const VIEW_TRACKED_STORAGE_PREFIX = 'zep-zem-charge-view-tracked:';
const FAILURE_TRACKED_STORAGE_PREFIX = 'zep-zem-charge-failure-tracked:';
const HISTORY_CHARGE_FLOW_ID_KEY = 'zepZemChargeFlowId';

const parseZemChargePlace = (
  value: string | null,
): ZemChargePopupPlace | undefined =>
  ZEM_CHARGE_POPUP_PLACES.includes(value as ZemChargePopupPlace)
    ? (value as ZemChargePopupPlace)
    : undefined;

const parseZemChargeReason = (
  value: string | null,
): ZemChargeReason | undefined =>
  ZEM_CHARGE_REASONS.includes(value as ZemChargeReason)
    ? (value as ZemChargeReason)
    : undefined;

const parseNonNegativeNumber = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') return undefined;

  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
};

const readContextValue = <T>({
  searchParams,
  param,
  storageKey,
  parse,
  isFreshEntry,
}: {
  searchParams: URLSearchParams;
  param: string;
  storageKey: string;
  parse: (value: string | null) => T | undefined;
  isFreshEntry: boolean;
}): T | undefined => {
  if (searchParams.has(param)) {
    const value = parse(searchParams.get(param));
    if (value !== undefined) {
      window.sessionStorage.setItem(storageKey, String(value));
      return value;
    }

    window.sessionStorage.removeItem(storageKey);
    return undefined;
  }

  if (isFreshEntry) {
    window.sessionStorage.removeItem(storageKey);
    return undefined;
  }

  const storedValue = parse(window.sessionStorage.getItem(storageKey));
  if (storedValue === undefined) {
    window.sessionStorage.removeItem(storageKey);
  }
  return storedValue;
};

const isChargeEntryPage = () =>
  window.location.pathname.replace(/\/$/, '').endsWith('/accounts/charge');

export const getZemChargePopupContext = (): ZemChargePopupContext => {
  const searchParams = new URLSearchParams(window.location.search);
  const isPopup = searchParams.get('mode') === ZEM_CHARGE_POPUP_MODE;
  const historyChargeFlowId = isZemChargeFlowId(
    window.history.state?.[HISTORY_CHARGE_FLOW_ID_KEY],
  )
    ? window.history.state[HISTORY_CHARGE_FLOW_ID_KEY]
    : undefined;
  const isFreshEntry =
    searchParams.has(ZEM_CHARGE_POPUP_OPENER_ORIGIN_PARAM) ||
    (isChargeEntryPage() &&
      !searchParams.has(ZEM_CHARGE_FLOW_ID_PARAM) &&
      !historyChargeFlowId);

  const openerOrigin = readContextValue({
    searchParams,
    param: ZEM_CHARGE_POPUP_OPENER_ORIGIN_PARAM,
    storageKey: ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY,
    parse: value => value ?? undefined,
    isFreshEntry: false,
  });

  const requestedChargeFlowId = readContextValue({
    searchParams,
    param: ZEM_CHARGE_FLOW_ID_PARAM,
    storageKey: ZEM_CHARGE_FLOW_ID_STORAGE_KEY,
    parse: value => (isZemChargeFlowId(value) ? value : undefined),
    isFreshEntry,
  });
  const chargeFlowId =
    requestedChargeFlowId ?? historyChargeFlowId ?? createZemChargeFlowId();
  window.sessionStorage.setItem(ZEM_CHARGE_FLOW_ID_STORAGE_KEY, chargeFlowId);
  if (isChargeEntryPage() && !searchParams.has(ZEM_CHARGE_FLOW_ID_PARAM)) {
    window.history.replaceState(
      {
        ...window.history.state,
        [HISTORY_CHARGE_FLOW_ID_KEY]: chargeFlowId,
      },
      '',
    );
  }

  const commerceFlowId = readContextValue({
    searchParams,
    param: ZEM_CHARGE_COMMERCE_FLOW_ID_PARAM,
    storageKey: ZEM_CHARGE_COMMERCE_FLOW_ID_STORAGE_KEY,
    parse: value => (isZemChargeFlowId(value) ? value : undefined),
    isFreshEntry,
  });
  const place = readContextValue({
    searchParams,
    param: ZEM_CHARGE_POPUP_PLACE_PARAM,
    storageKey: ZEM_CHARGE_POPUP_PLACE_STORAGE_KEY,
    parse: parseZemChargePlace,
    isFreshEntry,
  });
  const chargeReason = readContextValue({
    searchParams,
    param: ZEM_CHARGE_REASON_PARAM,
    storageKey: ZEM_CHARGE_REASON_STORAGE_KEY,
    parse: parseZemChargeReason,
    isFreshEntry,
  });
  const requiredZem = readContextValue({
    searchParams,
    param: ZEM_CHARGE_REQUIRED_ZEM_PARAM,
    storageKey: ZEM_CHARGE_REQUIRED_ZEM_STORAGE_KEY,
    parse: parseNonNegativeNumber,
    isFreshEntry,
  });
  const balanceZem = readContextValue({
    searchParams,
    param: ZEM_CHARGE_BALANCE_ZEM_PARAM,
    storageKey: ZEM_CHARGE_BALANCE_ZEM_STORAGE_KEY,
    parse: parseNonNegativeNumber,
    isFreshEntry,
  });
  const shortfallZem = readContextValue({
    searchParams,
    param: ZEM_CHARGE_SHORTFALL_ZEM_PARAM,
    storageKey: ZEM_CHARGE_SHORTFALL_ZEM_STORAGE_KEY,
    parse: parseNonNegativeNumber,
    isFreshEntry,
  });

  const trackingQuery = {
    [ZEM_CHARGE_FLOW_ID_PARAM]: chargeFlowId,
    ...(commerceFlowId && {
      [ZEM_CHARGE_COMMERCE_FLOW_ID_PARAM]: commerceFlowId,
    }),
    ...(place && { [ZEM_CHARGE_POPUP_PLACE_PARAM]: place }),
    ...(chargeReason && { [ZEM_CHARGE_REASON_PARAM]: chargeReason }),
    ...(requiredZem !== undefined && {
      [ZEM_CHARGE_REQUIRED_ZEM_PARAM]: String(requiredZem),
    }),
    ...(balanceZem !== undefined && {
      [ZEM_CHARGE_BALANCE_ZEM_PARAM]: String(balanceZem),
    }),
    ...(shortfallZem !== undefined && {
      [ZEM_CHARGE_SHORTFALL_ZEM_PARAM]: String(shortfallZem),
    }),
  };

  return {
    chargeFlowId,
    ...(commerceFlowId && { commerceFlowId }),
    ...(place && { place }),
    ...(chargeReason && { chargeReason }),
    ...(requiredZem !== undefined && { requiredZem }),
    ...(balanceZem !== undefined && { balanceZem }),
    ...(shortfallZem !== undefined && { shortfallZem }),
    query: {
      ...(isPopup && { mode: ZEM_CHARGE_POPUP_MODE }),
      ...(isPopup &&
        openerOrigin && {
          [ZEM_CHARGE_POPUP_OPENER_ORIGIN_PARAM]: openerOrigin,
        }),
      ...trackingQuery,
    },
  };
};

const markTracked = (storagePrefix: string, id: string): boolean => {
  if (!id) return false;

  const storageKey = `${storagePrefix}${id}`;
  if (window.sessionStorage.getItem(storageKey)) {
    return false;
  }

  window.sessionStorage.setItem(storageKey, '1');
  return true;
};

export const markZemChargeCompleteTracked = (orderId: string): boolean =>
  markTracked(COMPLETE_TRACKED_STORAGE_PREFIX, orderId);

export const markZemChargeViewTracked = (chargeFlowId: string): boolean =>
  markTracked(VIEW_TRACKED_STORAGE_PREFIX, chargeFlowId);

export const markZemChargeFailureTracked = (
  chargeFlowId: string,
  code: string,
  eventName: 'web_zem_charge_cancel' | 'web_zem_charge_error',
): boolean =>
  markTracked(
    FAILURE_TRACKED_STORAGE_PREFIX,
    `${chargeFlowId}:${eventName}:${code}`,
  );
